import * as THREE from 'three';
import { EXTRUDED_PROFILE_SURFACE_BIAS } from '../../_data/frameExtent.js';
import { resolveConstellationPlacement } from './constellationPlacement.js';
import { resolveEffectiveEntityShape } from './effectiveEntityShape.js';
import { resolveConnectorEndpoint, resolveLayoutAnchorWorldPosition } from './entityAnchorResolution.js';
import { connectorOrbitBounds } from './connectorOrbit.js';
import { boxExtentPoints, geometryExtentPoints } from './geometryExtent.js';
import { geometrySpecSpacingDiameter, resolveCylinderWallGeometrySpecs, resolveExtrudedProfileGeometrySpecs, resolveItemGeometrySpec, resolveItemOutlineGeometrySpec, } from './geometrySpec.js';
import { resolveLabel, toDeclaredPlacementOffset } from './labelResolution.js';
import { computeSectorRingLabelGeometryByItemKey } from './sectorRingLabelGeometry.js';
import { resolveSectorSliceCompression, sectorSliceHalfHeight } from './sectorSliceSurface.js';
import { toothedBandMaxHalfHeight, toothedBandMaxRadius } from './toothedBand.js';
import { resolveSidePairAnchorEndpoint } from './sidePairAnchorResolution.js';
import { flattenItemParametricState, resolveConnectorBracketPoints, resolveConnectorCurveControlPoint, resolveConnectorCurveDirection, resolveConnectorCurveMinOffset, resolveConnectorCurvePlane, resolveConnectorCurveStrength, resolveConnectorPathKind, } from './spaceRendererHelpers.js';
import { normalizeSpokePanelLabelOffset } from './spokePanelLabelTransform.js';
export { resolveItemFlag, resolveConnectorContourPaths } from './itemContour.js';
/**
 * Per-glyph advance used for label extents. The renderer has no font metrics before the font
 * loads, so this deliberately exceeds the 0.62 em it uses for label backgrounds.
 */
export const FRAME_BOUNDS_LABEL_GLYPH_WIDTH_EM = 0.7;
const LABEL_CHANNELS = ['aboveItem', 'onItem', 'backItem'];
// Mirrors the renderer's panel label resolver; only the position affects bounds.
const PANEL_LABEL_POSITION_RESOLVER = ({ channel, declaration }) => {
    if (channel !== 'onItem')
        return undefined;
    return { position: normalizeSpokePanelLabelOffset(toDeclaredPlacementOffset(declaration.placement)) };
};
function unsupported(kind, value) {
    throw new Error(`Unsupported ${kind} for frame bounds: ${String(value)}`);
}
function length(vector) {
    return Math.hypot(vector[0], vector[1], vector[2]);
}
function sphere(center, radius, source) {
    if (!center.every(Number.isFinite) || !Number.isFinite(radius) || radius < 0) {
        throw new Error(`Non-finite frame bounds for ${source}`);
    }
    return { center: [center[0], center[1], center[2]], radius };
}
/** Radius around the item origin enclosing the geometry a spec builds, under any rotation. */
export function geometrySpecLocalExtent(spec) {
    const kind = spec.geometryKind;
    switch (kind) {
        case 'revolved-profile':
            return Math.max(...spec.profile.map(([radius, height]) => Math.hypot(radius, height)));
        case 'toothed-band':
            return Math.hypot(toothedBandMaxRadius(spec), toothedBandMaxHalfHeight(spec));
        case 'sphere':
            return spec.radius;
        case 'panel':
            if (spec.curvatureRadius !== undefined)
                return Math.max(...geometryExtentPoints(spec, undefined).map(length));
            return Math.hypot(spec.width, spec.height) / 2;
        case 'panel-outline':
            if (spec.curvatureRadius !== undefined)
                return Math.max(...geometryExtentPoints(spec, undefined).map(length));
            return Math.hypot(spec.width / 2, spec.height / 2, spec.z);
        case 'sector-slice-outline':
        case 'sector-slice':
            return Math.hypot(spec.outerRadius, sectorSliceHalfHeight(spec.innerRadius, spec.outerRadius, spec.outerThickness));
        case 'annular-band':
            return spec.outerRadius;
        case 'torus':
            return spec.ringRadius + spec.tubeRadius;
        case 'cylinder-wall':
            // Unit height is scaled by the shape height; see `resolveShapeLocalExtent`.
            return Math.hypot(spec.radius, 0.5);
        case 'connector-tube':
            return Math.hypot(0.5, 0.5);
        case 'extruded-profile':
            // Cross-section only; the extrusion length is added by `resolveShapeLocalExtent`.
            return Math.hypot(spec.innerWidth / 2 + spec.lipThickness, spec.lipHeight + spec.floorDepth + EXTRUDED_PROFILE_SURFACE_BIAS);
        default: {
            const exhaustive = kind;
            return unsupported('geometry kind', exhaustive);
        }
    }
}
function assertSupportedLayout(layout) {
    const kind = layout.layoutKind;
    switch (kind) {
        case 'ring':
        case 'sector-ring':
        case 'spoke':
        case 'crescent':
        case 'concentric-ring':
            return;
        default: {
            const exhaustive = kind;
            unsupported('layout kind', exhaustive);
        }
    }
}
/** Local extent of an item, following the same shape-to-component routing as the renderer. */
function resolveItemLocalExtent(shape, spec) {
    if (shape === undefined)
        return geometrySpecLocalExtent(spec);
    const kind = shape.shapeKind;
    switch (kind) {
        case 'cylinder-wall':
            return Math.max(0, ...resolveCylinderWallGeometrySpecs(shape)
                .map((wallSpec) => Math.hypot(wallSpec.radius, shape.height / 2)));
        case 'extruded-profile':
            return Math.max(length(shape.from), length(shape.to))
                + Math.max(...resolveExtrudedProfileGeometrySpecs(shape).map(geometrySpecLocalExtent));
        case 'sphere':
        case 'panel':
        case 'sector-slice':
        case 'annular-band':
        case 'revolved-profile':
        case 'ring-guide':
        case 'line-guide': {
            const outline = resolveItemOutlineGeometrySpec(shape);
            return Math.max(geometrySpecLocalExtent(spec), outline ? geometrySpecLocalExtent(outline) : 0);
        }
        default: {
            const exhaustive = kind;
            return unsupported('shape kind', exhaustive);
        }
    }
}
function estimateLabelExtent(text, fontSize, padding) {
    const width = Math.max(1, text.length) * fontSize * FRAME_BOUNDS_LABEL_GLYPH_WIDTH_EM + padding * 2;
    const height = fontSize + padding * 2;
    return Math.hypot(width, height) / 2;
}
function resolveItemLabelExtent(args) {
    const { item, shape, itemParametricState, itemPlacement, sectorRingLabelGeometry, hoverFontScale, anchorContext } = args;
    const labels = itemParametricState?.label;
    if (labels === undefined)
        return 0;
    // Channel visibility depends on hover/activation, so every declared channel is bounded.
    return Math.max(0, ...LABEL_CHANNELS.flatMap((channel) => {
        const declaration = labels[channel];
        if (declaration === undefined)
            return [];
        return [1, hoverFontScale].flatMap((fontScale) => {
            const resolved = resolveLabel({
                item,
                channel,
                declaration,
                shape,
                itemOpacity: 1,
                anchorContext,
                labelAnchorWorldPosition: itemPlacement.labelAnchorWorldPosition,
                ...(channel === 'onItem' && sectorRingLabelGeometry ? { layoutOverride: sectorRingLabelGeometry } : {}),
                ...(itemPlacement.labelFrameRotation ? { labelFrameRotation: itemPlacement.labelFrameRotation } : {}),
                ...(shape?.shapeKind === 'panel' ? { transformResolver: PANEL_LABEL_POSITION_RESOLVER } : {}),
                hoverFontScale: fontScale,
            });
            if (resolved === null)
                return [];
            let extent = resolved.flag ? Math.hypot(Math.max(1, resolved.text.length) * resolved.fontSize * FRAME_BOUNDS_LABEL_GLYPH_WIDTH_EM + resolved.padding * 2, (resolved.fontSize + resolved.padding * 2) / 2) : estimateLabelExtent(resolved.text, resolved.fontSize, resolved.padding);
            if (resolved.flag?.prongOffsets) {
                const post = resolved.flag.postOffset;
                extent = Math.max(extent, length(post), ...resolved.flag.prongOffsets.map(offset => Math.hypot(post[0] + offset[0], post[1] + offset[1], post[2] + offset[2])));
                const radius = length(resolved.position) + extent;
                args.collectPoints?.(boxExtentPoints([0, 0, 0], [radius, radius, radius]));
                return [radius];
            }
            if (resolved.billboard && !resolved.flag && args.collectBillboard) {
                args.collectBillboard(resolved.position, (Math.max(1, resolved.text.length) * resolved.fontSize * FRAME_BOUNDS_LABEL_GLYPH_WIDTH_EM + resolved.padding * 2) / 2, (resolved.fontSize + resolved.padding * 2) / 2, resolved.rotation);
            }
            else
                args.collectPoints?.(boxExtentPoints(resolved.position, [extent, extent, extent]));
            return [length(resolved.position) + extent];
        });
    }));
}
function collectEntityConstellationSpheres(args) {
    const { constellation, items, entityConstellationParametricStateByKey, itemParametricStateByKey, hoverFontScale, anchorContext } = args;
    const extentPoints = args.extentPoints;
    const constellationState = entityConstellationParametricStateByKey[constellation.constellationKey];
    const layout = constellationState?.layout;
    if (!layout || items.length === 0)
        return [];
    assertSupportedLayout(layout);
    const itemCount = items.length;
    const stateByItemKey = new Map(items.map((item) => [
        item.itemKey,
        itemParametricStateByKey[item.itemKey],
    ]));
    const shapeByItemKey = new Map(items.map((item) => [
        item.itemKey,
        resolveEffectiveEntityShape({ layout, itemParametricState: stateByItemKey.get(item.itemKey), itemCount }),
    ]));
    const specByItemKey = new Map(items.map((item, itemIndex) => [
        item.itemKey,
        resolveItemGeometrySpec({ layout, shape: shapeByItemKey.get(item.itemKey), itemIndex, itemCount }),
    ]));
    const placement = resolveConstellationPlacement({
        constellation,
        constellationState,
        layout,
        items,
        shapeByItemKey,
        layoutNormalOffsetByItemKey: new Map(items.map((item) => [
            item.itemKey,
            stateByItemKey.get(item.itemKey)?.layoutNormalOffset,
        ])),
        spokeSpacingFallback: geometrySpecSpacingDiameter(specByItemKey.get(items[0].itemKey)),
        context: anchorContext,
    });
    // The renderer draws nothing for an unresolvable placement, so there is nothing to bound.
    if (!placement)
        return [];
    const sectorRingLabelGeometryByItemKey = layout.layoutKind === 'sector-ring'
        ? computeSectorRingLabelGeometryByItemKey({ layout, items, labelLayoutPolicy: constellationState?.labelLayoutPolicy })
        : null;
    const itemByKey = new Map(items.map((item) => [item.itemKey, item]));
    const source = `constellation ${constellation.constellationKey}`;
    const frameRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.frameRotation))
        .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...placement.itemFrameRotation)));
    const framePosition = new THREE.Vector3(...placement.framePosition);
    const transformedPoint = new THREE.Vector3();
    const itemRotation = new THREE.Euler();
    const itemPosition = new THREE.Vector3();
    let transformedItem;
    const transform = (point, itemPlacement, rotateItem = true) => {
        const vector = transformedPoint.set(...point);
        if (itemPlacement) {
            if (transformedItem !== itemPlacement) {
                transformedItem = itemPlacement;
                itemRotation.set(...itemPlacement.rotation);
                itemPosition.set(...itemPlacement.position);
            }
            if (rotateItem)
                vector.applyEuler(itemRotation);
            vector.add(itemPosition);
        }
        vector.applyQuaternion(frameRotation).add(framePosition);
        const result = vector.toArray();
        if (!result.every(Number.isFinite))
            throw new Error(`Non-finite frame bounds for ${source}`);
        return result;
    };
    // Frame and item rotations pivot at the frame origin, so distances from it are rotation invariant.
    const circularExtents = new Set();
    const itemSpheres = placement.itemPlacements.flatMap((itemPlacement) => {
        const item = itemByKey.get(itemPlacement.itemKey);
        if (!item)
            return [];
        if (args.selectedItemKeys && !args.selectedItemKeys.has(item.itemKey))
            return [];
        const shape = shapeByItemKey.get(item.itemKey);
        const rotateItem = shape?.shapeKind !== 'extruded-profile' && shape?.shapeKind !== 'cylinder-wall';
        if (extentPoints) {
            const spec = specByItemKey.get(item.itemKey);
            const outline = shape ? resolveItemOutlineGeometrySpec(shape) : null;
            // Sector cells share a full-circle envelope; spin cannot enlarge it. Keep distinct normal offsets.
            const circularKey = spec.geometryKind === 'sector-slice'
                && itemPlacement.rotation[0] === 0 && itemPlacement.rotation[2] === 0
                ? JSON.stringify([itemPlacement.position, spec.outerRadius, spec.outerThickness, outline?.geometryKind === 'sector-slice-outline'
                        ? outline.outerRadius : undefined]) : undefined;
            if (circularKey === undefined || !circularExtents.has(circularKey)) {
                if (circularKey)
                    circularExtents.add(circularKey);
                extentPoints.push(...[
                    ...geometryExtentPoints(spec, shape),
                    ...(outline ? geometryExtentPoints(outline, undefined) : []),
                ].map((point) => transform(point, itemPlacement, circularKey === undefined && rotateItem)));
            }
        }
        const localExtent = Math.max(resolveItemLocalExtent(shape, specByItemKey.get(item.itemKey)), resolveItemLabelExtent({
            item,
            shape,
            itemParametricState: shape === undefined
                ? stateByItemKey.get(item.itemKey)
                : { ...(stateByItemKey.get(item.itemKey) ?? {}), shape },
            itemPlacement,
            sectorRingLabelGeometry: sectorRingLabelGeometryByItemKey?.get(item.itemKey),
            hoverFontScale,
            anchorContext,
            ...(extentPoints ? { collectPoints: (points) => {
                    extentPoints.push(...points.map((point) => transform(point, itemPlacement, rotateItem)));
                } } : {}),
            ...(extentPoints && args.billboardDirection ? {
                collectBillboard: (position, halfWidth, halfHeight, rotation) => {
                    const direction = new THREE.Vector3(...args.billboardDirection);
                    direction.divideScalar(Math.max(Math.abs(direction.x), Math.abs(direction.y), Math.abs(direction.z))).normalize();
                    const matrix = new THREE.Matrix4().lookAt(direction, new THREE.Vector3(), new THREE.Vector3(0, 1, 0));
                    const right = new THREE.Vector3().setFromMatrixColumn(matrix, 0);
                    const up = new THREE.Vector3().setFromMatrixColumn(matrix, 1);
                    const back = new THREE.Vector3().setFromMatrixColumn(matrix, 2);
                    const center = new THREE.Vector3(...transform([0, 0, 0], itemPlacement, rotateItem))
                        .addScaledVector(right, position[0]).addScaledVector(up, position[1]).addScaledVector(back, position[2]);
                    const localRotation = new THREE.Euler(...rotation);
                    for (const x of [-halfWidth, halfWidth])
                        for (const y of [-halfHeight, halfHeight]) {
                            const corner = new THREE.Vector3(x, y, 0).applyEuler(localRotation);
                            extentPoints.push(center.clone().addScaledVector(right, corner.x)
                                .addScaledVector(up, corner.y).addScaledVector(back, corner.z).toArray());
                        }
                },
            } : {}),
        }));
        return [sphere(placement.framePosition, length(itemPlacement.position) + localExtent, `${source} item ${item.itemKey}`)];
    });
    const guideSpheres = !args.selectedItemKeys && placement.guide && placement.guide.points.length > 0
        ? [sphere(placement.framePosition, Math.max(...placement.guide.points.map(length)), `${source} guide`)]
        : [];
    if (extentPoints && placement.guide && !args.selectedItemKeys) {
        extentPoints.push(...placement.guide.points.map((point) => transform(point)));
    }
    return [...itemSpheres, ...guideSpheres];
}
function collectConnectorSpheres(args) {
    const { items, itemParametricStateByKey, anchorContext } = args;
    return items.flatMap((item) => {
        const state = itemParametricStateByKey[item.itemKey];
        const resolveEndpoint = (endpointRole) => resolveConnectorEndpoint({
            endpoint: resolveSidePairAnchorEndpoint({
                parametricState: state,
                endpointRole,
                endpoint: item.endpoints[endpointRole],
                context: anchorContext,
            }),
            context: anchorContext,
            positionOverride: state?.endpointPositions?.[endpointRole],
        });
        const from = resolveEndpoint('from');
        const to = resolveEndpoint('to');
        // Matches the renderer, which skips connectors with an unresolvable endpoint.
        if (!from || !to)
            return [];
        if (state?.endpointOrbit) {
            return connectorOrbitBounds(from.position, to.position, state.endpointOrbit)
                .map(point => sphere(point, 0, `connector ${item.itemKey} orbit`));
        }
        const control = resolveConnectorPathKind(state) === 'curved'
            ? resolveConnectorCurveControlPoint({
                start: from.position,
                end: to.position,
                curvePlane: resolveConnectorCurvePlane(state),
                curveStrength: resolveConnectorCurveStrength(state),
                curveMinOffset: resolveConnectorCurveMinOffset(state),
                curveDirection: resolveConnectorCurveDirection(state),
            })
            : null;
        // A quadratic Bezier stays inside the hull of its control points.
        const points = resolveConnectorPathKind(state) === 'bracket'
            ? resolveConnectorBracketPoints({ start: from.position, end: to.position, parametricState: state })
            : control ? [from.position, control, to.position] : [from.position, to.position];
        return points.map((point) => sphere(point, 0, `connector ${item.itemKey}`));
    });
}
/**
 * Conservative world-space spheres for everything the scene renderer draws for a frame: entity
 * items (geometry, outlines and labels, once per constellation that lays them out), layout guides
 * and connectors. Visibility rules are ignored on purpose so the bounds do not change with hover or
 * activation. No scene resources are allocated. Unknown layout, shape or geometry kinds throw.
 */
export function collectFrameBoundingSpheres(frame) {
    return collectFrameBounds(frame);
}
/**
 * Placed geometry envelopes, labels and connector hulls, without enclosing empty opposite space.
 * billboardDirection opts into camera-facing rectangles for a fixed orientation, using world Y as up.
 * Omit it for envelopes shared by cameras whose viewing orientation changes.
 */
export function collectFrameExtentPoints(frame, options) {
    validateBillboardDirection(options?.billboardDirection);
    const points = [];
    collectFrameBounds(frame, points, options?.billboardDirection);
    return points;
}
export function collectEntityTargetExtentPoints(frame, selectedItemKeys) {
    return prepareFrameBoundsChunks(frame, true, undefined, selectedItemKeys)
        .flatMap(chunk => chunk.collect(chunk.context).points);
}
/**
 * The placed frame of a sector-ring constellation, or null when it lays out no items or its anchor does not resolve,
 * since the renderer then draws nothing. Other layouts throw.
 */
export function resolveSectorRingFrame(frame, constellationKey) {
    const entityConstellations = frame.constellations.filter((entry) => entry.constellationKind === 'entity');
    const constellation = entityConstellations.find(entry => entry.constellationKey === constellationKey);
    if (!constellation)
        throw new Error(`Constellation '${constellationKey}' is not present.`);
    const entityConstellationParametricStateByKey = frame.constellationParametricState?.entityConstellationByKey ?? {};
    const layout = entityConstellationParametricStateByKey[constellation.constellationKey]?.layout;
    if (layout?.layoutKind !== 'sector-ring')
        throw new Error(`Constellation '${constellationKey}' has no sector-ring layout.`);
    const entityItemsByCollectionKey = new Map();
    for (const collection of frame.itemCollections) {
        if (collection.itemCollectionKind === 'entity')
            entityItemsByCollectionKey.set(collection.itemCollectionKey, collection.items);
    }
    if (!entityItemsByCollectionKey.get(constellation.itemCollectionKey)?.length)
        return null;
    const position = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context: {
            entityConstellations, entityItemsByCollectionKey, entityConstellationParametricStateByKey,
            itemParametricStateByKey: flattenItemParametricState(frame.itemParametricState),
        } });
    if (!position)
        return null;
    const innerRadius = layout.innerRadius ?? 0, outerRadius = layout.outerRadius, thickness = layout.outerThickness ?? 0;
    const halfHeight = (radius) => thickness > 0 && radius > 0
        ? sectorSliceHalfHeight(innerRadius, outerRadius, thickness, radius, layout.thicknessProfile) : 0;
    return { position: [...position], rotation: [...(layout.rotation ?? [0, 0, 0])], innerRadius, outerRadius,
        innerHalfHeight: halfHeight(innerRadius), outerHalfHeight: halfHeight(outerRadius) };
}
export function geometryItemState(state) {
    if (!state)
        return state;
    const { visualStyle: _style, ...geometry } = state;
    return geometry;
}
/** The angular slice occupied by an item, in its parent ring's placed frame. */
export function resolveSectorRingItemFrame(frame, itemKey) {
    const collection = frame.itemCollections.find(entry => entry.itemCollectionKind === 'entity'
        && entry.items.some(item => item.itemKey === itemKey));
    const constellation = collection && frame.constellations.find((entry) => entry.constellationKind === 'entity'
        && entry.itemCollectionKey === collection.itemCollectionKey);
    if (!collection || !constellation)
        throw new Error(`Contour item '${itemKey}' has no parent constellation.`);
    const layout = frame.constellationParametricState?.entityConstellationByKey?.[constellation.constellationKey]?.layout;
    const shape = resolveEffectiveEntityShape({ layout, itemCount: collection.items.length,
        itemParametricState: frame.itemParametricState?.entityItemByKey?.[itemKey] });
    if (layout?.layoutKind !== 'sector-ring' || shape?.shapeKind !== 'sector-slice') {
        throw new Error(`Contour item '${itemKey}' requires a sector-ring layout.`);
    }
    const ring = resolveSectorRingFrame(frame, constellation.constellationKey);
    if (!ring)
        return null;
    const angle = collection.items.findIndex(item => item.itemKey === itemKey) / collection.items.length * Math.PI * 2;
    const active = frame.spaceRenderOptions?.activation?.activeItemKeyByConstellationKey?.[constellation.constellationKey] === itemKey;
    const scale = resolveSectorSliceCompression(active, layout.activeThicknessScale, frame.itemParametricState?.entityItemByKey?.[itemKey]?.thicknessScale);
    return { ...ring, innerHalfHeight: ring.innerHalfHeight * scale, outerHalfHeight: ring.outerHalfHeight * scale,
        startAngle: angle + shape.startAngle, endAngle: angle + shape.endAngle };
}
export function validateBillboardDirection(direction) {
    if (direction && (!direction.every(Number.isFinite) || direction.length !== 3
        || !Number.isFinite(Math.hypot(...direction)) || Math.hypot(...direction) === 0)) {
        throw new Error('Billboard extent direction must have three finite coordinates and a finite nonzero length.');
    }
}
function collectFrameBounds(frame, extentPoints, billboardDirection) {
    return prepareFrameBoundsChunks(frame, extentPoints !== undefined, billboardDirection).flatMap((chunk) => {
        const result = chunk.collect(chunk.context);
        extentPoints?.push(...result.points);
        return result.spheres;
    });
}
export function prepareFrameBoundsChunks(frame, includeExtentPoints, billboardDirection, selectedItemKeys) {
    const entityItemsByCollectionKey = new Map();
    const connectorItemsByCollectionKey = new Map();
    for (const collection of frame.itemCollections) {
        if (collection.itemCollectionKind === 'entity') {
            entityItemsByCollectionKey.set(collection.itemCollectionKey, collection.items);
        }
        else if (collection.itemCollectionKind === 'connector') {
            connectorItemsByCollectionKey.set(collection.itemCollectionKey, collection.items);
        }
        else {
            unsupported('item collection kind', collection.itemCollectionKind);
        }
    }
    const entityConstellations = [];
    const connectorConstellations = [];
    for (const constellation of frame.constellations) {
        if (constellation.constellationKind === 'entity')
            entityConstellations.push(constellation);
        else if (constellation.constellationKind === 'connector')
            connectorConstellations.push(constellation);
        else
            unsupported('constellation kind', constellation.constellationKind);
    }
    const itemParametricStateByKey = flattenItemParametricState(frame.itemParametricState);
    const entityConstellationParametricStateByKey = frame.constellationParametricState?.entityConstellationByKey ?? {};
    const anchorContext = {
        entityConstellations,
        entityItemsByCollectionKey,
        itemParametricStateByKey,
        entityConstellationParametricStateByKey,
    };
    const hoverFontScale = Math.max(1, frame.spaceRenderOptions?.labelProximity?.hoverFontScale ?? 1);
    return [
        ...entityConstellations.map((constellation) => {
            const items = entityItemsByCollectionKey.get(constellation.itemCollectionKey) ?? [];
            const collect = (points, context) => collectEntityConstellationSpheres({
                constellation, items, entityConstellationParametricStateByKey, itemParametricStateByKey,
                hoverFontScale, anchorContext: context,
                ...(points ? { extentPoints: points } : {}),
                ...(billboardDirection ? { billboardDirection } : {}),
                ...(selectedItemKeys ? { selectedItemKeys } : {}),
            });
            return { key: `entity:${constellation.constellationKey}`, input: {
                    constellation, items,
                    states: items.map((item) => geometryItemState(itemParametricStateByKey[item.itemKey])),
                    constellationState: entityConstellationParametricStateByKey[constellation.constellationKey],
                    hoverFontScale, billboardDirection, selectedItemKeys,
                }, context: anchorContext, collect: (context) => {
                    const points = [];
                    const spheres = collect(includeExtentPoints ? points : undefined, context);
                    return { spheres, points };
                } };
        }),
        ...(selectedItemKeys ? [] : connectorConstellations).map((constellation) => {
            const items = connectorItemsByCollectionKey.get(constellation.itemCollectionKey) ?? [];
            const collect = (points, context) => {
                const spheres = collectConnectorSpheres({ items, itemParametricStateByKey, anchorContext: context });
                points?.push(...spheres.map(({ center }) => center));
                return spheres;
            };
            return { key: `connector:${constellation.constellationKey}`, input: {
                    constellation, items,
                    states: items.map((item) => geometryItemState(itemParametricStateByKey[item.itemKey])),
                }, context: anchorContext, collect: (context) => {
                    const points = [];
                    const spheres = collect(includeExtentPoints ? points : undefined, context);
                    return { spheres, points };
                } };
        }),
    ];
}
/** Smallest sphere centred on the spheres' bounding-box centre that encloses all of them. */
export function encloseBoundingSpheres(spheres) {
    if (spheres.length === 0)
        return null;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const { center, radius } of spheres) {
        for (let axis = 0; axis < 3; axis += 1) {
            min[axis] = Math.min(min[axis], center[axis] - radius);
            max[axis] = Math.max(max[axis], center[axis] + radius);
        }
    }
    const center = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const radius = Math.max(...spheres.map((entry) => Math.hypot(entry.center[0] - center[0], entry.center[1] - center[1], entry.center[2] - center[2]) + entry.radius));
    return { center, radius };
}
/** Conservative world bounding sphere of a frame, or `null` when it renders nothing. */
export function deriveFrameBoundingSphere(frame) {
    return encloseBoundingSpheres(collectFrameBoundingSpheres(frame));
}
